import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Box,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useState, useEffect, useRef } from 'react'

import { getProgramEnrolmentPauseEligibility } from 'api/programs'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { eventEmitter, eventAlert } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%'
  },
  noteBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  noteText: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1)
  },
  noteContent: {
    fontSize: '14px',
    color: '#856404'
  }
}))

const PauseEnrolmentForm = ({
  isModalOpen,
  formData,
  onSaveClick,
  closeModal
}) => {
  const classes = useStyles()
  const [formValues, setFormValues] = useState({
    start_date: '',
    selected_days: '',
    end_date: '',
    reason: ''
  })
  const [errors, setErrors] = useState({})
  const [pauseDurationOptions, setPauseDurationOptions] = useState([])
  const [isLoadingEligibility, setIsLoadingEligibility] = useState(true)
  const hasFetchedEligibilityRef = useRef(false)

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value
    }))

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null
      }))
    }

    if (field === 'selected_days' && formValues.start_date) {
      const startDate = dayjs(formValues.start_date)
      const days = parseInt(value)
      const endDate = startDate.add(days - 1, 'day')
      const maxEnd = formData?.end_date
        ? dayjs(formData.end_date).subtract(7, 'day')
        : null

      setFormValues((prev) => ({
        ...prev,
        end_date: endDate.format('YYYY-MM-DD')
      }))

      if (maxEnd && endDate.isAfter(maxEnd)) {
        setErrors((prev) => ({
          ...prev,
          selected_days: `End date cannot be after ${maxEnd.format(
            'YYYY-MM-DD'
          )}`
        }))
      }
    }

    // Calculate resume date when start date changes
    if (field === 'start_date' && formValues.selected_days) {
      const startDate = dayjs(value)
      const days = parseInt(formValues.selected_days)
      const endDate = startDate.add(days - 1, 'day')
      const maxEnd = formData?.end_date
        ? dayjs(formData.end_date).subtract(7, 'day')
        : null

      setFormValues((prev) => ({
        ...prev,
        end_date: endDate.format('YYYY-MM-DD')
      }))

      if (maxEnd && endDate.isAfter(maxEnd)) {
        setErrors((prev) => ({
          ...prev,
          selected_days: `End date cannot be after ${maxEnd.format(
            'YYYY-MM-DD'
          )}`
        }))
      }
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formValues.start_date) {
      newErrors.start_date = 'Pause start date is required'
    }

    if (!formValues.selected_days) {
      newErrors.selected_days = 'Pause duration is required'
    }

    // reason is optional

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (validateForm()) {
      onSaveClick(
        {
          start_date: formValues.start_date,
          end_date: formValues.end_date,
          reason: formValues.reason
        },
        'pause_enrolment'
      )
    } else {
      // Inform ModalForm to stop the saving state when validation fails
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
    }
  }

  useEffect(() => {
    if (!isModalOpen || !formData?.uuid) return
    if (hasFetchedEligibilityRef.current) return

    hasFetchedEligibilityRef.current = true

    const checkPauseEligibility = async () => {
      try {
        setIsLoadingEligibility(true)
        const response = await getProgramEnrolmentPauseEligibility({
          enrolment_uuid: formData?.uuid
        })

        if (response?.success && response?.data?.pause_eligible) {
          const options = (response?.data?.pause_options || []).map((opt) => ({
            value: String(opt.days),
            label: opt.label,
            days: opt.days
          }))
          setPauseDurationOptions(options)
        } else {
          const reason = response?.data?.reason || 'Not eligible for pausing.'
          eventAlert(reason, constants.ERROR)
          closeModal()
        }
      } catch (error) {
        console.error('Error checking pause eligibility:', error)
        eventAlert(
          'Unable to check pause eligibility. Please try again.',
          constants.ERROR
        )
        closeModal()
      } finally {
        setIsLoadingEligibility(false)
      }
    }

    checkPauseEligibility()
  }, [isModalOpen, formData?.uuid, closeModal])

  useEffect(() => {
    if (!isModalOpen) {
      hasFetchedEligibilityRef.current = false
    }
  }, [isModalOpen])

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      onSaveClick={handleSave}
      modalTitle="Pause subscription"
      buttonLabel="Confirm"
      maxWidth="lg"
      loading={isLoadingEligibility}>
      <TextField
        className={classes.formControl}
        name="start_date"
        label="Pause start date"
        type="date"
        value={formValues.start_date}
        onChange={(e) => handleInputChange('start_date', e.target.value)}
        variant="outlined"
        required
        error={!!errors.start_date}
        helperText={errors.start_date}
        InputLabelProps={{
          shrink: true
        }}
        inputProps={{
          min: formData?.start_date || undefined,
          max: formData?.end_date
            ? dayjs(formData.end_date).subtract(7, 'day').format('YYYY-MM-DD')
            : undefined
        }}
      />

      <FormControl
        className={classes.formControl}
        variant="outlined"
        error={!!errors.selected_days}
        required>
        <InputLabel>Pause duration</InputLabel>
        <Select
          name="selected_days"
          value={formValues.selected_days}
          onChange={(e) => handleInputChange('selected_days', e.target.value)}
          label="Pause duration"
          disabled={isLoadingEligibility}>
          {pauseDurationOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {errors.selected_days && (
          <FormHelperText>{errors.selected_days}</FormHelperText>
        )}
      </FormControl>

      <TextField
        className={classes.formControl}
        name="end_date"
        label="End date"
        value={formValues.end_date}
        variant="outlined"
        disabled
        InputLabelProps={{
          shrink: true
        }}
      />

      <TextField
        className={classes.formControl}
        name="reason"
        label="Reason for pause"
        value={formValues.reason}
        onChange={(e) => handleInputChange('reason', e.target.value)}
        variant="outlined"
        error={!!errors.reason}
        helperText={errors.reason}
        placeholder="Please provide a reason for pausing the subscription..."
      />

      <Box className={classes.noteBox}>
        <Typography className={classes.noteContent}>
          <span className={classes.noteText}>Note: </span>
          Any sessions booked in the selected pause duration will be
          automatically cancelled.
        </Typography>
      </Box>
    </ModalForm>
  )
}

PauseEnrolmentForm.propTypes = {
  isModalOpen: PropTypes.bool,
  formData: PropTypes.object,
  onSaveClick: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired
}

export default PauseEnrolmentForm
